<template>
    <page :busy="busy">
        <b-container fluid class="my-3 reservation-invoice" v-if="reservationInvoice">
            <b-row class="control-panel-button-row">
                <b-col md="12">
                    <div>
                        <b-button
                            class="w-sm-100 control-panel-button"
                            size="lg"
                            variant="primary"
                            @click="goToControlPanel()"
                            :disabled="busy"
                            >{{ controlPanelButtonLabel }} &nbsp;&nbsp;<font-awesome-icon
                                v-if="!redirectToInbox"
                                rotation="90"
                                :icon="['fas', 'sliders']" /><font-awesome-icon v-else :icon="['fas', 'envelope']"
                        /></b-button>
                    </div>
                </b-col>
            </b-row>
            <b-row>
                <div class="col-print-12" :class="{ 'wrapper-invoice': !isMedium, 'col-md-12': isMedium }">
                    <div :class="{ 'main-invoice': !isMedium }">
                        <reservation-invoice-form
                            :initial-values="{
                                reservationInvoice: reservationInvoice,
                            }"
                            ref="reservationInvoiceForm"
                            @refresh-reservation-invoice="refreshReservationInvoice"
                        ></reservation-invoice-form>
                        <b-row v-if="largeRegenerate && !isCancelled">
                            <b-col md="12">
                                <b-button
                                    class="w-sm-100 regenerate-button"
                                    variant="primary"
                                    @click="$bvModal.show('regenerateInvoiceConfirm')"
                                    :disabled="busy"
                                    size="lg"
                                    >{{ getRegenerateButtonLabel() }}&nbsp;&nbsp;<font-awesome-icon
                                        :icon="['fas', 'file-invoice']"
                                /></b-button>
                            </b-col>
                        </b-row>
                    </div>
                </div>
                <div class="col-print-12" :class="{ 'sidebar-invoice': !isMedium, 'col-md-12': isMedium }">
                    <reservation-invoice-sidebar
                        :initial-values="{
                            reservationInvoice: reservationInvoice,
                        }"
                        ref="reservationInvoiceSidebar"
                        @refresh-reservation-invoice="refreshReservationInvoice"
                    ></reservation-invoice-sidebar>
                </div>
            </b-row>
            <b-row v-if="!largeRegenerate && !isCancelled">
                <b-col md="12">
                    <b-button
                        class="w-sm-100 regenerate-button"
                        variant="primary"
                        @click="$bvModal.show('regenerateInvoiceConfirm')"
                        :disabled="busy"
                        size="lg"
                        >{{ getRegenerateButtonLabel() }}&nbsp;&nbsp;<font-awesome-icon :icon="['fas', 'file-invoice']"
                    /></b-button>
                </b-col>
            </b-row>
        </b-container>
        <modal
            id="regenerateInvoiceConfirm"
            header-bg-variant="warning"
            header-text-variant="light"
            ok-variant="primary"
            title="Confirm Regeneration"
            ok-title="Yes"
            @ok="regenerateInvoice()"
            :static="true"
        >
            <div>
                Invoice regeneration will reset all charges, fees & deposits to their original state. All recorded
                payments & refunds will not be affected. Do you wish to continue?
            </div>
        </modal>
        <modal
            id="ignoreAddRemoveDayWarningModal"
            header-bg-variant="warning"
            header-text-variant="light"
            ok-variant="primary"
            title="Warning"
            ok-title="Proceed Anyway"
            @ok="proceedAddRemoveDay()"
            :static="true"
        >
            <div>
                <ul>
                    <li v-for="e in addRemoveDayParams.warnings">{{ e }}</li>
                </ul>
            </div>
        </modal>
        <modal
            id="addRemoveDayErrorModal"
            header-bg-variant="danger"
            header-text-variant="light"
            ok-variant="primary"
            title="Error"
            ok-title="OK"
            ok-only
            :static="true"
        >
            <div>
                <ul>
                    <li v-for="e in addRemoveDayParams.errors">{{ e }}</li>
                </ul>
            </div>
        </modal>
        <modal
            id="changeRatesModal"
            header-bg-variant="info"
            header-text-variant="light"
            ok-variant="primary"
            title="Change Rates"
            ok-title="OK"
            ok-only
            :static="true"
        >
            <div>Apply rate change to all dates for this property / room?</div>
            <template #modal-footer="{ ok, cancel }">
                <div>
                    <b-button variant="primary" @click="changeRate(true)" class="change-rates-yes-button mr-1">
                        Yes
                    </b-button>
                    <b-button variant="success" @click="changeRate(false)" class="mr-1">
                        No, change selected date only
                    </b-button>
                    <b-button variant="secondary" @click="cancel()"> Cancel </b-button>
                </div>
            </template>
        </modal>
        <invoice-print v-if="reservationInvoice" :invoiceValues="reservationInvoice" />
    </page>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Page from '../../generic/page/page.vue';
    import Reservation from '../reservation.vue';
    import ReservationInvoiceForm from './reservation-invoice-form.vue';
    import ReservationInvoiceSidebar from './reservation-invoice-sidebar.vue';
    import InvoicePrint from './print/invoice-print.vue';
    import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';
    import { invoiceMixin } from '../mixins/invoice-mixin';
    import Modal from '../../generic/modal/modal.vue';
    import API from '../../../api';
    import { formatResponseError } from '../../../utils/api';
    import {
        RESERVATION_INVOICE_CHANGE_RATE_ENDPOINT,
        RESERVATION_INVOICE_CHANGE_CANCELLATION_FEE_ENDPOINT,
        RESERVATION_INVOICE_REGENERATE_ENDPOINT,
        RESERVATION_INVOICE_ADD_DAY_ENDPOINT,
        RESERVATION_INVOICE_REMOVE_DAY_ENDPOINT,
    } from '../../../endpoints';

    export default {
        //extends: Reservation,
        name: 'ReservationInvoice',
        components: {
            ReservationInvoiceForm,
            ReservationInvoiceSidebar,
            Modal,
            Page,
            InvoicePrint,
        },
        mixins: [invoiceMixin, removeScrollMixin],
        created() {
            this.onResize();
            window.addEventListener('resize', this.onResize);
        },
        computed: {
            ...mapGetters('reservation', ['reservationInvoice']),
            reservationId() {
                return parseInt(this.$route.params.id);
            },
            isQuote() {
                return this.reservationInvoice.reservation_status.state === 'QUOTE';
            },
            isCancelled() {
                return this.reservationInvoice.reservation_status.state === 'CANCELLED';
            },
        },
        data() {
            return {
                addRemoveDayParams: { errors: [], warnings: [] },
                changeRateParams: {},
                largeRegenerate: true,
                isMedium: false,
            };
        },
        provide() {
            return {
                addRemoveDay: this.addRemoveDay,
                changeRateConfirm: this.changeRateConfirm,
                changeCancellationFee: this.changeCancellationFee,
            };
        },
        mounted() {
            this.$store.commit('reservation/resetReservationInvoice');

            (async () => {
                this.busy = true;
                try {
                    await Promise.all([
                        this.fetchAutomatedPaymentSettings(),
                        this.fetchReservationInvoice(this.reservationId),
                    ]);
                } finally {
                    this.busy = false;
                }
            })();
        },
        beforeUnmount() {
            window.removeEventListener('resize', this.onResize);
            this.$store.commit('reservation/resetReservationInvoice');
        },
        methods: {
            async fetchAutomatedPaymentSettings() {
                try {
                    await this.$store.dispatch('paymentGateway/fetchAutomatedPaymentSettings');
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not fetch automated payment settings.' });
                }
            },
            async addRemoveDay(operation, isStartDate, isEndDate, ignoreWarnings) {
                let url = '';
                if (operation == 'ADD') {
                    url = RESERVATION_INVOICE_ADD_DAY_ENDPOINT(this.reservationId);
                } else {
                    url = RESERVATION_INVOICE_REMOVE_DAY_ENDPOINT(this.reservationId);
                }

                this.addRemoveDayParams.operation = operation;
                this.addRemoveDayParams.ignoreWarnings = ignoreWarnings;
                this.addRemoveDayParams.isStartDate = isStartDate;
                this.addRemoveDayParams.isEndDate = isEndDate;

                this.busy = true;
                try {
                    await API.patch(url, {
                        ignore_warnings: this.addRemoveDayParams.ignoreWarnings,
                        is_start_date: this.addRemoveDayParams.isStartDate,
                        is_end_date: this.addRemoveDayParams.isEndDate,
                    });
                    await this.fetchReservationInvoice(this.reservationId);
                    if (operation == 'ADD') {
                        this.successToast({ text: 'Reservation day successfully added.' });
                    } else {
                        this.successToast({ text: 'Reservation day successfully removed.' });
                    }
                } catch (e) {
                    if (e.response.status === 400) {
                        if (e.response.data.error_code && e.response.data.error_code == 'WARNING') {
                            this.addRemoveDayParams.warnings = e.response.data.errors;
                            this.$bvModal.show('ignoreAddRemoveDayWarningModal');
                        } else if (e.response.data.error_code && e.response.data.error_code == 'ERROR') {
                            this.addRemoveDayParams.errors = e.response.data.errors;
                            this.$bvModal.show('addRemoveDayErrorModal');
                        }
                    } else {
                        this.errorToast({ text: `Could not add/remove reservation day.` });
                    }
                } finally {
                    this.busy = false;
                }
            },
            proceedAddRemoveDay() {
                this.addRemoveDay(
                    this.addRemoveDayParams.operation,
                    this.addRemoveDayParams.isStartDate,
                    this.addRemoveDayParams.isEndDate,
                    true
                );
            },
            async changeRateConfirm(techResID, date, newRate, oldRate, duration) {
                this.changeRateParams.techResID = techResID;
                this.changeRateParams.date = date;
                this.changeRateParams.newRate = newRate;
                this.changeRateParams.oldRate = oldRate;
                this.changeRateParams.duration = duration;
                this.$bvModal.show('changeRatesModal');
            },
            async changeRate(wholeReservation) {
                this.$bvModal.hide('changeRatesModal');

                this.busy = true;
                try {
                    await API.patch(RESERVATION_INVOICE_CHANGE_RATE_ENDPOINT(this.reservationId), {
                        tech_res_id: this.changeRateParams.techResID,
                        date: this.changeRateParams.date,
                        new_rate: this.changeRateParams.newRate,
                        old_rate: this.changeRateParams.oldRate,
                        duration: this.changeRateParams.duration,
                        whole_reservation: wholeReservation,
                    });
                    await this.fetchReservationInvoice(this.reservationId);
                    this.successToast({ text: 'Rate(s) successfully changed.' });
                } catch (e) {
                    this.errorToast({
                        text: e.response?.data?.errors || 'Error: Could not change rates(s).',
                    });
                } finally {
                    this.busy = false;
                }
            },
            async changeCancellationFee(id, amount) {
                this.busy = true;
                try {
                    await API.patch(RESERVATION_INVOICE_CHANGE_CANCELLATION_FEE_ENDPOINT(this.reservationId), {
                        id: id,
                        amount: amount,
                    });
                    await this.fetchReservationInvoice(this.reservationId);
                    this.successToast({ text: 'Cancellation Fee successfully changed.' });
                } catch (e) {
                    this.errorToast({
                        text: e.response?.data?.errors || 'Error: Could not change Cancellation Fee.',
                    });
                } finally {
                    this.busy = false;
                }
            },
            async regenerateInvoice() {
                this.$bvModal.hide('regenerateInvoiceConfirm');

                this.busy = true;
                try {
                    await API.patch(RESERVATION_INVOICE_REGENERATE_ENDPOINT(this.reservationId));
                    await this.fetchReservationInvoice(this.reservationId);
                    this.successToast({ text: 'Invoice successfully regenerated.' });
                } catch (e) {
                    this.errorToast({
                        text: 'Error: Could not regenerate invoice.',
                    });
                } finally {
                    this.busy = false;
                }
            },
            onResize() {
                if (window.innerWidth >= 992) {
                    this.largeRegenerate = true;
                } else {
                    this.largeRegenerate = false;
                }

                this.isMedium = window.innerWidth < 992;
            },
            getRegenerateButtonLabel() {
                if (this.isQuote) {
                    return 'Regenerate Quote';
                }
                return 'Regenerate Invoice';
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    .change-rates-yes-button {
        width: 80px;
    }

    .control-panel-button-row {
        margin-bottom: 15px !important;
    }

    :deep(.regenerate-button) {
        font-weight: bold !important;
        align-items: center;
        justify-content: center;
        display: flex !important;
    }

    :deep(.control-panel-button) {
        font-weight: bold !important;
        align-items: center;
        justify-content: center;
        display: inline-flex !important;
    }

    .wrapper-invoice {
        float: left;
        width: 100%;
    }

    .main-invoice {
        margin-right: 455px;
        clear: both;
        /* overflow: auto; */
        padding: 0 10px;
    }

    .sidebar-invoice {
        position: relative;
        width: 450px;
        margin-left: -455px;
        float: right;
        overflow: hidden;
    }

    @include media-breakpoint-down(md) {
        :deep(.w-sm-100) {
            width: 100% !important;
        }
        :deep(.control-panel-button-row) {
            display: block !important;
        }
    }

    @include media-breakpoint-down(lg) {
        :deep(.control-panel-button-row) {
            display: block !important;
        }
    }

    @include media-breakpoint-up(lg) {
        :deep(.control-panel-button-row) {
            display: none !important;
        }
    }

    @media (max-width: 1400px) {
        .main-invoice {
            margin-right: 405px;
        }

        .sidebar-invoice {
            width: 400px;
            margin-left: -405px;
        }
    }

    @media (max-width: 1200px) {
        .main-invoice {
            margin-right: 355px;
        }

        .sidebar-invoice {
            width: 350px;
            margin-left: -355px;
        }
    }

    @media print {
        .reservation-invoice {
            display: none;
        }
    }
</style>
